import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Alert,
  Autocomplete,
} from '@mui/material';
import { styled } from '@mui/system';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import Logo from '../../../Assets/full_duck_stacked_blue.png';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  backgroundColor: theme.palette.background.paper,
  boxShadow: 24,
  padding: theme.spacing(4),
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  outline: 'none',
}));

const StyledAutocompleteInput = styled(TextField)({
  '& .MuiInputBase-root': {
    width: '100px',
    height: '40px',
    backgroundColor: '#6a6a6a',
    borderRadius: '4px',
    paddingRight: '0px !important',
    '& input': {
      color: 'white',
      textAlign: 'center',
      padding: '12px 10px',
      '&::placeholder': {
        color: 'white',
        opacity: 0.85,
      },
    },
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiSvgIcon-root': {
        color: 'white',
      },
    },
  },
  '& .MuiInput-underline:before': { borderBottom: 'none' },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
  '& .MuiInput-underline:after': { borderBottom: 'none' },
});

const GrayButton = styled(Button)({
  backgroundColor: '#6a6a6a',
  color: 'white',
  padding: '12px 0',
  fontSize: '1.2rem',
  fontWeight: 'bold',
  width: '100%',
  textTransform: 'none',
  borderRadius: '4px',
});

const monthOptions = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const dayOptions = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
const currentYearForOptions = new Date().getFullYear();
const yearOptions = Array.from({ length: currentYearForOptions - 1925 + 1 }, (_, i) =>
  String(currentYearForOptions - i)
);

const AgeVerificationModal = ({ open, onConfirm }) => {
  const [month, setMonth] = useState(null);
  const [day, setDay] = useState(null);
  const [year, setYear] = useState(null);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');

  const isDateValidAndOver21 = () => {
    if (!month || !day || !year) {
      return false;
    }

    const dateString = `${year}-${month}-${day}`;
    const dob = dayjs(dateString, 'YYYY-MM-DD', true);

    if (!dob.isValid()) {
      return false;
    }

    const twentyOneYearsAgo = dayjs().subtract(21, 'year');
    return (
      dob.isSameOrAfter(dayjs(dob).subtract(0, 'day')) &&
      dob.isBefore(twentyOneYearsAgo.add(1, 'day'))
    );
  };

  const handleEnterClick = () => {
    setError('');

    if (!month || !day || !year) {
      setError('Please select a month, day, and year.');
      return;
    }
    const dateString = `${year}-${month}-${day}`;
    const dob = dayjs(dateString, 'YYYY-MM-DD', true);

    if (!dob.isValid()) {
      setError('Please enter a valid date (MM/DD/YYYY).');
      return;
    }

    if (!isDateValidAndOver21()) {
      setError('You must be at least 21 years old to enter.');
      return;
    }

    onConfirm(rememberMe);
  };

  const isButtonDisabled = !month || !day || !year || (year && year.length < 4);

  return (
    <Modal open={open}>
      <ModalBox>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1, width: '100%' }}>
          <img src={Logo} alt='Logo' style={{ maxWidth: '100px', height: 'auto' }} />
        </Box>

        <Typography variant='h4' sx={{ mb: 1, fontWeight: 'bold' }}>
          Age Verification
        </Typography>

        <Typography variant='body1' sx={{ mb: 3 }}>
          You must be over the age of 21 to view this content
        </Typography>

        {error && (
          <Alert severity='error' sx={{ mb: 2, width: '100%' }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
          <Autocomplete
            value={month}
            onChange={(event, newValue) => {
              setMonth(newValue);
              setError('');
            }}
            options={monthOptions}
            disableClearable
            renderInput={(params) => (
              <StyledAutocompleteInput {...params} placeholder='MONTH' variant='standard' />
            )}
          />
          <Autocomplete
            value={day}
            onChange={(event, newValue) => {
              setDay(newValue);
              setError('');
            }}
            options={dayOptions}
            disableClearable
            renderInput={(params) => (
              <StyledAutocompleteInput {...params} placeholder='DAY' variant='standard' />
            )}
          />
          <Autocomplete
            value={year}
            onChange={(event, newValue) => {
              setYear(newValue);
              setError('');
            }}
            options={yearOptions}
            disableClearable
            renderInput={(params) => (
              <StyledAutocompleteInput {...params} placeholder='YEAR' variant='standard' />
            )}
          />
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              name='rememberMe'
            />
          }
          label='Remember me for 30 days'
          sx={{ mb: 1.5, alignSelf: 'flex-start', ml: -1 }}
        />

        <Typography
          variant='caption'
          sx={{
            mb: 3,
            textAlign: 'justify',
            fontSize: '0.75rem',
            lineHeight: 1.4,
          }}
        >
          By accessing this website, you confirm that you are of legal age to purchase firearms in
          your state of residence. Please note that state, county, and local restrictions may apply.
          Firearms that are not legally available in all 50 states will not be advertised via SMS.
        </Typography>

        <GrayButton variant='contained' onClick={handleEnterClick} disabled={isButtonDisabled}>
          Enter
        </GrayButton>
      </ModalBox>
    </Modal>
  );
};

export default AgeVerificationModal;
